import type { CustomLocationType } from './address';
import type {
  GetRidesByDeactivatedPartnerCallersQuery,
  GetPrefilledRideAddressesQuery,
  GetNumberOfRidesPerUserQuery,
  LeadQuery,
} from '@/gql/graphql';
import type { MEASUREMENT_SYSTEM } from './app';

export type PaginatedRideTypeResponse = Extract<GetRidesByDeactivatedPartnerCallersQuery['getRidesByDeactivatedPartnerCallers'], {}>;
export type Ride = Extract<PaginatedRideTypeResponse['rows'], {}>[number];
export type ExtendedRide = Ride & {
  index: number;
  customerName?: string;
};

export type RidePrefilledAddresses = Extract<GetPrefilledRideAddressesQuery['getPrefilledRideAddresses'], {}>;

export type PaginatedNumberOfRidesPerUserTypeResponse = Extract<GetNumberOfRidesPerUserQuery['getNumberOfRidesPerUser'], {}>;
export type NumberOfRidesPerUser = Extract<PaginatedNumberOfRidesPerUserTypeResponse['rows'], {}>[number];

export type RideCost = Extract<LeadQuery['lead']['rideCosts'], {}>[number];
export interface RideType {
  badDrivers?: boolean;
  overrideBigCar?: boolean;
  overrideSmallCar?: boolean;
  overrideBannedCar?: boolean;
  isDoNotApplySubsidy?: boolean;
  isDoNotApplyExpiringCredits?: boolean;
}

export interface OrderDataType {
  phoneNumber?: string;
  riderPhone?: string;
  customRiderName?: string;
  rideName?: string;
  typeOfCar?: string;
  partnerRidePurpose?: string;
  serviceType?: string;
  rideMultiple?: boolean;
  isWillCall?: boolean;
  noteToDriver?: string;
  isNoNoteToDriver?: boolean;
  notesForLP?: string;
  isNeededToMonitorRide?: boolean;
  rideType?: RideType;
  callToConfirmSurge?: string;
  pickupDate?: string;
  pickupTime?: string;
  repeatEveryXWeek?: number;
  startAddress?: CustomLocationType;
  endAddress?: CustomLocationType;
  day0?: boolean;
  day1?: boolean;
  day2?: boolean;
  day3?: boolean;
  day4?: boolean;
  day5?: boolean;
  day6?: boolean;
  endOn?: string;
  endOnType?: string;
  endAfterXTimes?: number;
  minutesBeforeCustomReminder?: number;
  minutesAfterCreationCustomReminder?: number;
  isExpandDriverSearch?: boolean;
  isExpandVehicleSearch?: boolean;
  driverNotePickUp?: string;
  driverNoteDropOff?: string;
}

export interface RideInfoItem {
  id: string /* key of ride object */;
  label: string /* label to show */;
  value: string /* ride info item value */;
  type?: 'text' | 'image';
}

export enum MedicalTripResult {
  VALIDATION_PASSED = 'passed',
  NO_VALIDATION_NEEDED = 'skipped',
  USER_DECLINED = 'declined',
  VALIDATION_FAILED = 'failed',
  NO_MEDICAL_PLACE = 'no_medical_place',
}

export enum RideServiceType {
  Transportation = 'transportation',
  PharmacyDelivery = 'pharmacy',
  SpecialRequest = 'special-request',
}

export enum FulfillmentIndicator {
  GREEN = 'green',
  RED = 'red',
  YELLOW = 'yellow',
}

export type RideEstimate = {
  firstName: string;
  lastName: string;
  rideType: string;
  surgeFactor: number;
  eta: number;
  duration: number;
  distance: number;
  lowEstimate: number;
  highEstimate: number;
  callRideFromEstimateLink: string;
  credits: string;
  partnerSubsidy?: string;
  partnerSubsidyEstimate?: number | null;
  partnerCostEstimate?: number;
  expiringCreditInfo?: {
    minAmount?: number;
    maxAmount?: number;
    description: string;
    showMembershipCreditBreakdown?: boolean;
    coveredByCreditMin?: number;
    coveredByCreditMax?: number;
    callerPaysMin?: number;
    callerPaysMax?: number;
  };
  isNoReturnTrip: boolean;
  returnTripEtaMins?: number;
  riderName: string;
  etaText: string;
  estimate?: string;
  warningMessage: string;
  isAutoTip: boolean;
  lowAutoTipAmount: number;
  highAutoTipAmount: number;
  medicalEstimate?: string;
  vendorEstimate?: string;
  minutesToAutoRetryRequest?: string;
  standardLowEstimate?: string;
  standardHighEstimate?: string;
  standardEstimate?: string;
  pricingExplanation?: string;
  operatorConfirmedSurgePriceWithCustomer?: boolean;
  rideCredits?: number;
  rideCostId: string;
  approximateEta?: string;
  currency?: string;
  perMile?: number;
  perMinute?: number;
  baseFare?: number;
  minimumFare?: number;
  operatingFee?: number;
  fareId?: string;
  customerCostOverride?: number;
  surgeKey?: string;
  surgeUrl?: string;
  fulfillmentIndicator?: string;
  storedLongRideOrderDataKey?: string;
  isDefaultOption?: boolean;
  providerName: string; // For nash
  fareDisplay?: string;
  multipler?: number;
  partnerInfoForOperator?: string;
  partnerCode?: string;
  units?: MEASUREMENT_SYSTEM;
};

export type DeliveryProofOption = 'photoProofRequired' | 'signatureProofRequired';

export type PaidFor = 'Yes' | 'No' | 'noPaymentRequired';

export type PharmacyDeliveryInfo = {
  storeName: string;
  storePhoneNumber: string;
  storeOpen: string;
  medicationName: string;
  controlledSubstance: string;
  paidFor: PaidFor | null;
  itemCount: number | null;
  patientName: string;
  birthday: Date | null;
  pickupInstructions: string;
  deliveryOptions: DeliveryProofOption | '';
  deliveryInstructions: string;
  driverTipDollars: number | null;
  notesForLP: string;
};

export type SpecialRequestDeliveryInfo = {
  storeName: string;
  storePhoneNumber: string;
  storeOpen: string;
  itemType: string;
  itemDescription: string;
  paidFor: PaidFor | null;
  itemCount: number | null;
  orderValueDollars: number | null;
  recipientName: string;
  pickupInstructions: string;
  deliveryOptions: DeliveryProofOption | '';
  deliveryInstructions: string;
  driverTipDollars: number | null;
  notesForLP: string;
};

export type DeliveryEstimatePayload = {
  storeName: string;
  isStoreOpen: boolean;
  paidFor: PaidFor;
  itemCount: number;
  valueCents: number;
  pickupInstructions?: string;
  deliveryInstructions?: string;
  tipAmountCents?: number;
  pickupPhoneNumber?: string;
  photoProofRequired?: boolean;
  signatureProofRequired?: boolean;
  medicationName?: string;
  isControlledSubstance?: boolean;
  patientName?: string; // pickupFirstName
  patientDateOfBirth?: string;
  itemType?: string;
  itemDescription?: string;
  recipientName?: string;
};

export type DeliveryInfoFormResult = {
  isValid: boolean;
  payload: DeliveryEstimatePayload | null;
  notesForLP: string;
};
